import React, { useMemo, useCallback } from 'react';
import { Table as TableComponent } from '@lucid/lego';
import type { VehicleOrder } from '../../../../api/services/lupp/order/importer/dto.importer';
import { useStore } from '../../../../store/store';
import { createLineItemColumns } from './utils';

type LineItemsProps = {
  theme: 'light' | 'dark';
  data: VehicleOrder[];
  totalCount: number;
  statusGroupCounts?: Record<string, number>;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onStatusFilterChange?: (value: string | null) => void;
  onVehicleOrderSelect: (record: VehicleOrder) => void;
  onAcceptSelected: (orders: VehicleOrder[]) => void;
  onRejectSingle: (order: VehicleOrder) => void;
  onGoToDelivery: (record: VehicleOrder) => void;
  onOrderConfirmationClick: (record: VehicleOrder) => void;
  isActioning?: boolean;
};

/**
 * Line Items table component for the Order Desk page.
 * Renders the list of vehicle order line items belonging to a selected importer order.
 * @component
 * @param props - Component props
 * @returns {JSX.Element}
 */
const LineItems: React.FC<LineItemsProps> = ({
  theme,
  data,
  totalCount,
  statusGroupCounts,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  sortColumn,
  sortDirection,
  onSort,
  onStatusFilterChange,
  onVehicleOrderSelect,
  onAcceptSelected,
  onRejectSingle,
  onGoToDelivery,
  onOrderConfirmationClick,
  isActioning = false,
}) => {
  const locale = useStore((state) => state.locale);

  const handleRowClick = useCallback(
    (record: VehicleOrder) => {
      onVehicleOrderSelect(record);
    },
    [onVehicleOrderSelect]
  );

  const columns = useMemo(
    () =>
      createLineItemColumns({
        theme,
        onRowClick: handleRowClick,
        labels,
        locale,
        onAcceptSingle: (order) => onAcceptSelected([order]),
        onRejectSingle,
        onGoToDelivery,
        onOrderConfirmationClick,
        isActioning,
      }),
    [
      theme,
      labels,
      handleRowClick,
      locale,
      onAcceptSelected,
      onRejectSingle,
      onGoToDelivery,
      onOrderConfirmationClick,
      isActioning,
    ]
  );

  const invertedTheme = theme === 'light' ? 'dark' : 'light';

  return (
    <TableComponent
      columns={columns}
      data={data}
      theme={invertedTheme}
      selection={{ enabled: false }}
      pagination={{
        enabled: true,
        total: totalCount,
        current: currentPage,
        pageSize,
        onChange: onPageChange,
        onPageSizeChange,
      }}
      search={{
        placeholder: labels?.['table.vehicleOrderSearchPlaceholder'] ?? 'Search Order # / VIN',
        value: searchQuery,
        onChange: onSearchChange,
      }}
      sort={{
        column: sortColumn,
        direction: sortDirection,
        onSort: onSort,
      }}
      columnConfig={{ enabled: true }}
      emptyState={{
        message: searchQuery.trim()
          ? (labels?.['table.noResults'] ?? 'No results found! Please update your query')
          : (labels?.['table.noVehicleOrders'] ?? 'No vehicle orders available'),
      }}
      labels={{
        manageColumns: labels?.['table.manageColumns'] ?? 'Manage Columns',
        showingItems:
          labels?.['table.showingItems'] ?? 'Showing {{start}}-{{end}} of {{total}} Items',
        pageOf: labels?.['table.pageOf'] ?? '{{current}} of {{total}}',
        columnsVisible:
          labels?.['table.columnsVisible'] ?? '{{visible}} of {{total}} columns visible',
        save: labels?.['table.save'] ?? 'Save',
      }}
      filterCards={{
        columnKey: 'statusName',
        totalCount: statusGroupCounts?.['ALL'] ?? totalCount,
        onChange: onStatusFilterChange,
        items: [
          {
            value: 'IN REVIEW',
            label: labels?.['status.inReview'] ?? 'In Review',
            count: statusGroupCounts?.['IN REVIEW'],
          },
          {
            value: 'WAITING FOR CONFIRMATION',
            label: labels?.['status.waitingForConfirmation'] ?? 'Waiting for Confirmation',
            count: statusGroupCounts?.['WAITING FOR CONFIRMATION'],
          },
          {
            value: 'CONFIRMED',
            label: labels?.['status.confirmed'] ?? 'Confirmed',
            count: statusGroupCounts?.['CONFIRMED'],
          },
          {
            value: 'READY FOR DISPATCH',
            label: labels?.['status.readyForDispatch'] ?? 'Ready for Pickup',
            count: statusGroupCounts?.['READY FOR DISPATCH'],
          },
          {
            value: 'FULFILLED',
            label: labels?.['status.fulfilled'] ?? 'Fulfilled',
            count: statusGroupCounts?.['FULFILLED'],
          },
          {
            value: 'DENIED / CANCELED',
            label: labels?.['status.deniedCanceled'] ?? 'Denied / Canceled',
            count: (statusGroupCounts?.['REJECTED'] ?? 0) + (statusGroupCounts?.['CANCELLED'] ?? 0),
          },
        ],
      }}
      gaComponent="line-items-table"
      dataTestId="line-items-table"
    />
  );
};

export default LineItems;
